import { useMemo, useState, useEffect, useRef, useCallback } from "react";
import type { RefObject } from "react";
import styled from "styled-components";
import { MdFullscreen, MdFullscreenExit } from "react-icons/md";
import { Form, Select } from "antd";
import { createPortal } from "react-dom";
import { useFilters } from "core/features/filters/store";
import { FiltrosAvancado } from "../../../interfaces/Planejamento";
import useRelatorioContext from "../../../hooks/useRelatorioContext";
import { FULLSCREEN_LAYER_Z_INDEX, SELECT_POPUP_Z_INDEX, formatarData } from "../utils";
import { useGetGraficoRelatorios } from "../hooks/useGetRelatorios";
import { Loader } from "./Loader";

const EMPTY_VALUE = "-";
const TOOLTIP_PAD = 14;
const PLANNED_LABEL_COLOR = "#4db380";
const DATE_IN_PLAN_COLOR = "#009157";
const DATE_OUT_OF_PLAN_COLOR = "#e53935";

type DateHighlight = "in" | "out" | null;

type GanttBar = {
  left: string;
  width: string;
  type: "planned" | "executed";
  exceeded?: boolean;
  label?: string;
};

type GanttTooltipRow = {
  area: string;
  start: string;
  end: string;
  startHighlight?: DateHighlight;
  endHighlight?: DateHighlight;
};

type GanttTooltipData = {
  operationClass: string;
  planned: GanttTooltipRow;
  executed: GanttTooltipRow;
};

type GanttRow = {
  operationClass: string;
  bars: GanttBar[];
  tooltip: GanttTooltipData;
};

type CronogramaClasse = {
  operacaoClasse: string;
  dataProgramadaInicio: string;
  dataProgramadaFim: string;
  dataProgramadaAjustadaInicio?: string;
  dataProgramadaAjustadaFim?: string;
  dataExecucaoInicio?: string;
  dataExecucaoFim?: string;
  areaPlanejada?: number;
  areaExecutada?: number;
  percentualExecutado: number;
};

type RelatorioGrafico = {
  cronogramaPorClasse?: CronogramaClasse[];
};

const MONTH_ABBR = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

function parseISODate(dateStr: string): Date {
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function formatMonthLabel(date: Date): string {
  const month = MONTH_ABBR[date.getMonth()];
  const year = String(date.getFullYear()).slice(-2);
  return `${month}/${year}`;
}

function getMonthsBetween(start: Date, end: Date): string[] {
  const months: string[] = [];
  const current = new Date(start.getFullYear(), start.getMonth(), 1);
  const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);

  while (current <= endMonth) {
    months.push(formatMonthLabel(current));
    current.setMonth(current.getMonth() + 1);
  }

  return months;
}

const MIN_BAR_WIDTH_PERCENT = 0.5;

function getBarStyle(start: Date, end: Date, timelineStart: Date, timelineEnd: Date): Pick<GanttBar, "left" | "width"> {
  const totalMs = timelineEnd.getTime() - timelineStart.getTime();
  if (totalMs <= 0) {
    return { left: "0%", width: "0%" };
  }

  const left = ((start.getTime() - timelineStart.getTime()) / totalMs) * 100;
  const right = ((end.getTime() - timelineStart.getTime()) / totalMs) * 100;
  const width = Math.max(right - left, MIN_BAR_WIDTH_PERCENT);

  return { left: `${left}%`, width: `${width}%` };
}

function buildExecutedBars(
  plannedStart: Date,
  plannedEnd: Date,
  execStart: Date,
  execEnd: Date,
  percentualExecutado: number,
  label: string,
  timelineStart: Date,
  timelineEnd: Date
): GanttBar[] {
  const bars: GanttBar[] = [];

  if (percentualExecutado <= 0 || execEnd < execStart) {
    return bars;
  }

  const pushSegment = (start: Date, end: Date, exceeded: boolean) => {
    if (end < start) return;

    bars.push({
      type: "executed",
      exceeded,
      ...getBarStyle(start, end, timelineStart, timelineEnd),
    });
  };

  if (execStart < plannedStart) {
    const anticipatedEnd = execEnd < plannedStart ? execEnd : plannedStart;
    pushSegment(execStart, anticipatedEnd, true);
  }

  const overlapStart = execStart > plannedStart ? execStart : plannedStart;
  const overlapEnd = execEnd < plannedEnd ? execEnd : plannedEnd;

  if (overlapStart <= overlapEnd) {
    pushSegment(overlapStart, overlapEnd, false);
  }

  if (execEnd > plannedEnd) {
    const exceededStart = execStart > plannedEnd ? execStart : plannedEnd;
    pushSegment(exceededStart, execEnd, true);
  }

  if (bars.length === 0) {
    pushSegment(execStart, execEnd, false);
  }

  if (bars.length > 0) {
    bars[bars.length - 1].label = label;
  }

  return bars;
}

function getPlannedDates(item: CronogramaClasse, typePlanning: string): { start: Date; end: Date } | null {
  const inicio =
    typePlanning === "ajustado"
      ? item.dataProgramadaAjustadaInicio ?? item.dataProgramadaInicio
      : item.dataProgramadaInicio;
  const fim =
    typePlanning === "ajustado"
      ? item.dataProgramadaAjustadaFim ?? item.dataProgramadaFim
      : item.dataProgramadaFim;

  if (!inicio || !fim) return null;

  return { start: parseISODate(inicio), end: parseISODate(fim) };
}

function formatArea(value?: number): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return EMPTY_VALUE;
  }

  return `${value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ha`;
}

function formatDateValue(iso?: string): string {
  if (!iso) return EMPTY_VALUE;
  return formatarData(iso);
}

function isDateInPlannedRange(date: Date, plannedStart: Date, plannedEnd: Date): boolean {
  const time = date.getTime();
  return time >= plannedStart.getTime() && time <= plannedEnd.getTime();
}

function getDateHighlight(iso: string | undefined, planned: { start: Date; end: Date } | null): DateHighlight {
  if (!iso || !planned) return null;

  const date = parseISODate(iso);
  return isDateInPlannedRange(date, planned.start, planned.end) ? "in" : "out";
}

function toIsoFromDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function buildTooltipData(item: CronogramaClasse, planned: { start: Date; end: Date } | null): GanttTooltipData {
  const areaPlanejada = item.areaPlanejada;
  const areaExecutada = item.areaExecutada;
  const isExecutedComplete =
    areaPlanejada != null &&
    areaExecutada != null &&
    areaExecutada >= areaPlanejada;

  const executedEndIso = isExecutedComplete ? item.dataExecucaoFim : undefined;

  return {
    operationClass: item.operacaoClasse,
    planned: {
      area: formatArea(areaPlanejada),
      start: planned ? formatDateValue(toIsoFromDate(planned.start)) : EMPTY_VALUE,
      end: planned ? formatDateValue(toIsoFromDate(planned.end)) : EMPTY_VALUE,
    },
    executed: {
      area: formatArea(areaExecutada),
      start: formatDateValue(item.dataExecucaoInicio),
      startHighlight: getDateHighlight(item.dataExecucaoInicio, planned),
      end: formatDateValue(executedEndIso),
      endHighlight: getDateHighlight(executedEndIso, planned),
    },
  };
}

function collectDatesFromCronograma(cronograma: CronogramaClasse[], typePlanning: string): Date[] {
  const dates: Date[] = [];

  cronograma.forEach((item) => {
    const planned = getPlannedDates(item, typePlanning);
    if (planned) {
      dates.push(planned.start, planned.end);
    }

    if (item.dataExecucaoInicio) dates.push(parseISODate(item.dataExecucaoInicio));
    if (item.dataExecucaoFim) dates.push(parseISODate(item.dataExecucaoFim));
  });

  return dates;
}

function buildGanttFromCronograma(
  cronograma: CronogramaClasse[],
  typePlanning: string
): { months: string[]; rows: GanttRow[] } {
  if (!cronograma.length) {
    return { months: [], rows: [] };
  }

  const allDates = collectDatesFromCronograma(cronograma, typePlanning);
  if (!allDates.length) {
    return { months: [], rows: [] };
  }

  const minDate = new Date(Math.min(...allDates.map((d) => d.getTime())));
  const maxDate = new Date(Math.max(...allDates.map((d) => d.getTime())));
  const timelineStart = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
  const timelineEnd = new Date(maxDate.getFullYear(), maxDate.getMonth() + 1, 0);
  const months = getMonthsBetween(timelineStart, timelineEnd);

  const rows = cronograma.map((item) => {
    const bars: GanttBar[] = [];
    const planned = getPlannedDates(item, typePlanning);

    if (planned) {
      bars.push({
        type: "planned",
        ...getBarStyle(planned.start, planned.end, timelineStart, timelineEnd),
      });

      if (item.dataExecucaoInicio && item.dataExecucaoFim) {
        const execStart = parseISODate(item.dataExecucaoInicio);
        const execEnd = parseISODate(item.dataExecucaoFim);
        const label = `${Math.round(item.percentualExecutado * 100)}%`;

        bars.push(
          ...buildExecutedBars(
            planned.start,
            planned.end,
            execStart,
            execEnd,
            item.percentualExecutado,
            label,
            timelineStart,
            timelineEnd
          )
        );
      }
    }

    return {
      operationClass: item.operacaoClasse,
      bars,
      tooltip: buildTooltipData(item, planned),
    };
  });

  return { months, rows };
}

function extractCronograma(data: RelatorioGrafico | RelatorioGrafico[] | undefined): CronogramaClasse[] {
  if (!data) return [];

  if (Array.isArray(data)) {
    return data[0]?.cronogramaPorClasse ?? [];
  }

  return data.cronogramaPorClasse ?? [];
}

function getBarZIndex(bar: GanttBar): number {
  if (bar.type === "planned") return 1;
  if (bar.exceeded && bar.label) return 3;
  return 2;
}

function DateCellValue({ value, highlight }: { value: string; highlight?: DateHighlight }) {
  const resolvedHighlight = value === EMPTY_VALUE ? null : (highlight ?? null);
  return <DateCell $highlight={resolvedHighlight}>{value}</DateCell>;
}

type CursorTooltipState = {
  visible: boolean;
  x: number;
  y: number;
  data: GanttTooltipData | null;
};

function positionCursorTooltip(
  clientX: number,
  clientY: number,
  tooltipEl: HTMLElement | null
): { x: number; y: number } {
  const rect = tooltipEl?.getBoundingClientRect();
  const width = rect?.width ?? 320;
  const height = rect?.height ?? 160;

  let x = clientX + TOOLTIP_PAD;
  let y = clientY + TOOLTIP_PAD;

  if (x + width > window.innerWidth - 8) {
    x = clientX - width - TOOLTIP_PAD;
  }
  if (y + height > window.innerHeight - 8) {
    y = clientY - height - TOOLTIP_PAD;
  }

  return { x: Math.max(8, x), y: Math.max(8, y) };
}

function GanttCursorTooltip({
  state,
  tooltipRef,
  zIndex,
}: {
  state: CursorTooltipState;
  tooltipRef: RefObject<HTMLDivElement>;
  zIndex: number;
}) {
  if (!state.visible || !state.data || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <CursorTooltipContainer ref={tooltipRef} style={{ left: state.x, top: state.y, zIndex }}>
      <GanttBarTooltipContent data={state.data} />
    </CursorTooltipContainer>,
    document.body
  );
}

function GanttBarTooltipContent({ data }: { data: GanttTooltipData }) {
  return (
    <TooltipBox>
      <TooltipTitle>{data.operationClass}</TooltipTitle>
      <TooltipTable>
        <thead>
          <tr>
            <th />
            <th>Área</th>
            <th>Data Inicial</th>
            <th>Data final</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><RowLabel $tone="planned">Planejado</RowLabel></td>
            <td>{data.planned.area}</td>
            <td>{data.planned.start}</td>
            <td>{data.planned.end}</td>
          </tr>
          <tr>
            <td><RowLabel $tone="executed">Realizado</RowLabel></td>
            <td>{data.executed.area}</td>
            <td>
              <DateCellValue value={data.executed.start} highlight={data.executed.startHighlight} />
            </td>
            <td>
              <DateCellValue value={data.executed.end} highlight={data.executed.endHighlight} />
            </td>
          </tr>
        </tbody>
      </TooltipTable>
    </TooltipBox>
  );
}

type Props = {
  filtrosAvancados?: FiltrosAvancado;
  permission: boolean;
};

function Grafico({ filtrosAvancados, permission }: Props) {
  const [typePlanning, setTypePlanning] = useState("inicial");
  const [periodoSelectKey, setPeriodoSelectKey] = useState(0);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [cursorTooltip, setCursorTooltip] = useState<CursorTooltipState>({
    visible: false,
    x: 0,
    y: 0,
    data: null,
  });
  const expandScreen = useRelatorioContext((s) => s.expandScreen);
  const setExpandScreen = useRelatorioContext((s) => s.setExpandScreen);
  const { listRelatoriosGraficos, isLoading, isFetching } = useGetGraficoRelatorios();
  const { Item } = Form;

  const selectPopupProps = expandScreen
    ? {
      dropdownStyle: { zIndex: SELECT_POPUP_Z_INDEX },
      getPopupContainer: () => document.body,
    }
    : {};

  const { filters: { filtersAdvanced }, updateField } = useFilters();

  const cronogramaPorClasse = useMemo(
    () => extractCronograma(listRelatoriosGraficos),
    [listRelatoriosGraficos]
  );

  const tooltipZIndex = expandScreen ? FULLSCREEN_LAYER_Z_INDEX + 1 : SELECT_POPUP_Z_INDEX;

  const showBarTooltip = useCallback((event: React.MouseEvent, data: GanttTooltipData) => {
    const { x, y } = positionCursorTooltip(event.clientX, event.clientY, tooltipRef.current);
    setCursorTooltip({ visible: true, x, y, data });
  }, []);

  const moveBarTooltip = useCallback((event: React.MouseEvent) => {
    setCursorTooltip((prev) => {
      if (!prev.visible) return prev;
      const { x, y } = positionCursorTooltip(event.clientX, event.clientY, tooltipRef.current);
      return { ...prev, x, y };
    });
  }, []);

  const hideBarTooltip = useCallback(() => {
    setCursorTooltip((prev) => ({ ...prev, visible: false }));
  }, []);

  const { months, rows } = useMemo(
    () => buildGanttFromCronograma(cronogramaPorClasse, typePlanning),
    [cronogramaPorClasse, typePlanning]
  );

  useEffect(() => {
    if (!filtersAdvanced?.safraPeriodo) {
      setPeriodoSelectKey((prev) => prev + 1);
    }
  }, [filtersAdvanced?.safraPeriodo]);

  function getDefaultValue() {
    const filtro = filtersAdvanced?.safraPeriodo;
    const filtros = filtrosAvancados?.safraPeriodos;
    if (!filtros?.some(f => f.safraPeriodoId === filtro)) {
      return undefined;
    }
    return filtro;
  }

  const content = (
    <Container expanded={expandScreen}>
      <ExpandButton disabled={!permission} expanded={expandScreen} onClick={() => setExpandScreen((prev) => !prev)} title={expandScreen ? "Sair da tela cheia" : "Expandir"}>
        {expandScreen ? <MdFullscreenExit size={20} /> : <MdFullscreen size={20} />}
      </ExpandButton>
      <Header>
        <ContentHeader>
          <div style={{ width: 280 }}>
            <Item
              label="Período de Produção"
              name="periodoProducao"
              rules={[{ required: true, message: "Período de Produção é obrigatório" }]}
            >
              <SelectAntd
                key={periodoSelectKey}
                {...selectPopupProps}
                onChange={(dataValue: any) => {
                  updateField("filtersAdvanced", {
                    ...filtersAdvanced,
                    safraPeriodo: dataValue
                  });
                }}
                placeholder="Período de Produção"
                defaultValue={getDefaultValue()}
                disabled={!permission}
                options={(filtrosAvancados?.safraPeriodos || [])
                  ?.map((item: any) => ({
                    label: item.nome,
                    value: item.safraPeriodoId,
                  }))}
              />
            </Item>
          </div>

        </ContentHeader>
        <ContentForm>
          <GraficoTypeSelector>
            <ButtonType disabled={!permission} onClick={() => setTypePlanning("inicial")} active={typePlanning === "inicial"}>
              Planejamento Inicial
            </ButtonType>
            <ButtonType disabled={!permission} onClick={() => setTypePlanning("ajustado")} active={typePlanning === "ajustado"}>
              Planejamento Ajustado
            </ButtonType>
          </GraficoTypeSelector>
        </ContentForm>
      </Header>

      {
        !isFetching && filtersAdvanced?.safraPeriodo && rows?.length === 0 && (
          <div style={{ textAlign: "center", padding: 24, color: "#888" }}>
            Sem dados
          </div>
        )
      }

      {
        !filtersAdvanced?.safraPeriodo && (
          <div style={{ textAlign: "center", padding: 24, color: "#888" }}>
            Selecione um período de produção para exibir o relatório
          </div>
        )
      }

      {permission ? isLoading || isFetching ? <Loader /> : (
        <>
          {filtersAdvanced?.safraPeriodo && rows?.length > 0 && (
            <GanttWrapper expanded={expandScreen}>
              <GanttTable>
                <thead>
                  <tr>
                    <th>CLASSE DE OPERAÇÃO</th>
                    {months.map((month) => (
                      <th key={month}>{month}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.operationClass}>
                      <td>{row.operationClass}</td>
                      <td colSpan={months.length || 1}>
                        <GanttCell>
                          {row.bars.map((bar, index) => (
                            <BarTooltipWrap
                              key={`${row.operationClass}-${bar.type}-${index}`}
                              $zIndex={getBarZIndex(bar)}
                              style={{ left: bar.left, width: bar.width }}
                              onMouseEnter={(event) => showBarTooltip(event, row.tooltip)}
                              onMouseMove={moveBarTooltip}
                              onMouseLeave={hideBarTooltip}
                            >
                              <Bar
                                barType={bar.type}
                                exceeded={bar.exceeded}
                                hasLabel={!!bar.label}
                              >
                                {bar.label ? <BarLabel
                                  exceeded={bar.exceeded}
                                  spancing={bar.label && parseFloat(bar.label) < 4 ? true : false}>{bar.label}
                                </BarLabel> : null}
                              </Bar>
                            </BarTooltipWrap>
                          ))}
                        </GanttCell>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </GanttTable>
            </GanttWrapper>
          )}
        </>
      ) : (
        <div style={{ textAlign: "center", padding: 24, color: "#888" }}>
          Você não tem permissão para visualizar este relatório
        </div>
      )}


      <GanttCursorTooltip state={cursorTooltip} tooltipRef={tooltipRef} zIndex={tooltipZIndex} />

      <Legend>
        <LegendItem>
          <LegendBox type="planned" />
          <span><strong>Planejado:</strong> Período planejado para execução</span>
        </LegendItem>
        <LegendItem>
          <LegendBox type="executed" />
          <span><strong>Executado:</strong> Área efetivamente realizada</span>
        </LegendItem>
        <LegendItem>
          <LegendBox type="exceeded" />
          <span><strong>Antecipado/Excedente:</strong> Execução fora do período planejado</span>
        </LegendItem>
      </Legend>
    </Container>
  );

  if (expandScreen && typeof document !== "undefined") {
    return createPortal(content, document.body);
  }

  return content;
}

const Container = styled.div<{ expanded: boolean }>`
  background: #fff;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: ${({ expanded }) => (expanded ? "0" : "8px")};
  padding: ${({ expanded }) => (expanded ? "20px" : "0")};
  z-index: ${({ expanded }) => (expanded ? FULLSCREEN_LAYER_Z_INDEX : "auto")};
  height: ${({ expanded }) => (expanded ? "100vh" : "auto")};
  max-height: ${({ expanded }) => (expanded ? "100vh" : "90vh")};

  ${({ expanded }) =>
    expanded &&
    `
    position: fixed;
    inset: 0;
    width: 100vw;
    height: 100vh;
  `}

  .ant-form-item {
    margin-bottom: 0;
  }
`;

const ExpandButton = styled.button<{ expanded: boolean }>`
  position: absolute;
  top: ${({ expanded }) => (expanded ? "10px" : "-10px")};
  right: ${({ expanded }) => (expanded ? "20px" : "0")};
  z-index: 20;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ContentForm = styled.div`
  display: flex;
  align-self: flex-end;
  flex-direction: column;
  align-items: center;
  justify-content: center;  
  gap: 10px;

`;

const ContentHeader = styled.div`
  display: flex;
  gap: 12px;

  .ant-form-item {
    display: flex !important;
    flex-direction: column !important;
    
    .ant-form-item-label {
      text-align: left !important;
    }
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;

   .ant-form-item-required::before {
      content: none !important;
    }

  .ant-form-item-required::after {
    display: inline-block !important;
    margin-left: 4px !important;
    color: red;
    font-size: 14px !important;
    line-height: 1 !important;
    content: "*" !important;
  }
`;

const GanttWrapper = styled.div<{ expanded: boolean }>`
  flex: 1;
  min-height: 0;
  overflow-x: auto;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 5px;
  }
`;

const GanttTable = styled.table`
  min-width: 100%;
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;

  th {
    /* min-width: 90px;
    width: 90px; */
    background: #f8f9fa;
    padding: 12px 10px;
    text-align: center;
    font-weight: 600;
    color: #555;
    border: 1px solid #e0e0e0;
    font-size: 11px;
    position: sticky;
    top: 0;
    z-index: 8;
  }

  th:first-child {
    min-width: 150px;
    width: 150px;
    position: sticky;
    left: 0;
    z-index: 12;
    background: #f8f9fa;
    box-shadow: 2px 0 4px rgba(0, 0, 0, 0.05);
  }

  td {
    border: 1px solid #e0e0e0;
    padding: 0;
    height: 50px;
    position: relative;
  }

  td:first-child {
    width: 150px;
    padding: 12px;
    font-weight: 500;
    color: #333;
    background: #fafafa;
    text-align: center;
    position: sticky;
    left: 0;
    z-index: 5;
    box-shadow: 2px 0 4px rgba(0, 0, 0, 0.05);
  }
`;

const GanttCell = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: visible;
`;

const BarTooltipWrap = styled.div<{ $zIndex: number }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  height: 22px;
  z-index: ${({ $zIndex }) => $zIndex};
`;

const Bar = styled.div<{ barType: "planned" | "executed"; exceeded?: boolean; hasLabel?: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 3px;
  background: ${({ barType, exceeded }) =>
    exceeded ? "#e53935" : barType === "planned" ? "#4db380" : "#009157"};
  opacity: ${({ barType }) => (barType === "planned" ? 0.35 : 1)};
  display: ${({ barType }) => (barType === "executed" ? "flex" : "block")};
  align-items: center;
  justify-content: flex-end;
  padding-right: 0;
  overflow: ${({ barType, hasLabel }) => (barType === "executed" && hasLabel ? "visible" : "hidden")};
  color: #fff;
  font-size: 11px;
  font-weight: bold;

  box-shadow: ${({ barType }) => (barType === "executed" ? "0 2px 4px rgba(0, 0, 0, 0.2)" : "none")};
`;

const BarLabel = styled.span<{ exceeded?: boolean, spancing?: boolean }>`
  white-space: nowrap;
  color: #fff;
  line-height: 1;

  ${({ spancing, exceeded }) => spancing && `
      position: absolute;
      right: 0;
      top: 50%;
      transform: translate(calc(100% + 6px), -50%);
      color: ${exceeded ? "#e53935" : "#009157"};
      background: transparent;
      padding: 0;
  `}
`;

const GraficoTypeSelector = styled.div`
  display: flex;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  overflow: hidden;
`;

const ButtonType = styled.button<{ active?: boolean }>`
  padding: 9px 16px;
  font-size: 12px;
  font-weight: 600;
  color: #666;
  background: #fff;
  border: none;
  cursor: pointer;
  transition: all 0.3s;

  ${props => !props.active && `
    &:hover {
      background: #f5f5f5;
    }
  `}

  ${props => props.active && `
    background: #009157;
    color: #fff;
  `}

  &:not(:last-child) {
    border-right: 2px solid #e0e0e0;
  }

  :disabled {
    background: #ddd !important;
    cursor: not-allowed !important;
    opacity: 0.5;
    color: #666;
  }
`;

const Legend = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 40px;
  padding: 10px 20px;
  border-top: 2px solid #e0e0e0;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.05);
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  color: #333;
`;

const LegendBox = styled.div<{ type: "planned" | "executed" | "exceeded" }>`
  width: 40px;
  height: 20px;
  border-radius: 3px;
  border: 1px solid #ddd;
  background: ${({ type }) => (type === "planned" ? "#4db380" : type === "executed" ? "#009157" : "#e53935")};
  opacity: ${({ type }) => (type === "planned" ? 0.35 : 1)};
`;

const CursorTooltipContainer = styled.div`
  position: fixed;
  pointer-events: none;
  max-width: 520px;
  background: #fff;
  color: #333;
  padding: 12px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

const TooltipBox = styled.div`
  min-width: 320px;
  color: #333;
`;

const TooltipTitle = styled.div`
  font-weight: 700;
  font-size: 13px;
  margin-bottom: 10px;
  color: #222;
  text-align: left;
`;

const TooltipTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
  border: 1px solid #f0f0f0;

  th,
  td {
    border: 1px solid #f0f0f0;
    padding: 6px 10px;
    text-align: center;
    white-space: nowrap;
  }

  th {
    background: #fafafa;
    color: rgba(0, 0, 0, 0.65);
    font-weight: 500;
  }

  td {
    background: #fff;
    color: #333;
  }

  td:first-child {
    text-align: left;
  }
`;

const RowLabel = styled.span<{ $tone: "planned" | "executed" }>`
  color: ${({ $tone }) => ($tone === "planned" ? PLANNED_LABEL_COLOR : DATE_IN_PLAN_COLOR)};
  font-weight: 600;
`;

const DateCell = styled.span<{ $highlight: DateHighlight }>`
  color: ${({ $highlight }) =>
    $highlight === "in" ? DATE_IN_PLAN_COLOR : $highlight === "out" ? DATE_OUT_OF_PLAN_COLOR : "inherit"};
  font-weight: ${({ $highlight }) => ($highlight ? 600 : 400)};
`;

const SelectAntd = styled(Select)`
  width: 100% !important;
  
  .ant-select-selector {
    height: 40px !important;
    border-radius: 8px !important;
    padding: 4px 11px !important;
  }

  .ant-form-item-label {
    text-align: left !important;
  }

   &.ant-select-multiple > .ant-select-selector {
    border: 1px solid #d9d9d9 !important;
     box-shadow: none !important;
     outline: none !important;
  }
  &.ant-select-multiple > .ant-select-selector:hover {
    border: 1px solid #40a9ff !important;
     box-shadow: none !important;
     outline: none !important;
  }
  &.ant-select-multiple.ant-select-open > .ant-select-selector {
    border-color: #40a9ff !important;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2) !important;
    border-right-width: 1px !important;
    outline: 0;
  }
`;

export default Grafico;